import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Spin,
  Card,
  Button,
  Typography,
  DatePicker,
  Input,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  MonitorOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { onError } from 'utils/handlers';
// import API from 'utils/api';
import CustomTable from 'components/Table';
import { DATE_FORMAT, DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import toCurrency from 'utils/toCurrency';
import Select from 'components/Select';
import NumericInput from 'components/NumericInput';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import { consultarRegistros, getPagosExternos } from 'api/recaudacion/pagosExternos';
import { getReceptoresDePagos } from 'api/catalogos/receptoresDePagos';
// import { getEstadosDePagosExternos } from 'api/catalogos/estadosDePagosExternos';
import Uploader from 'components/Uploader';
import { getTiposDeReferenciaDePagos } from 'api/catalogos/tiposDeReferenciasDePago';
import { getEstadosDePagosExternos } from 'api/catalogos/estadosDePagosExternos';
// import { getEstadosDePagosExternos } from 'api/catalogos/estadosDePagosExternos';
import usePermissionMessage from 'hooks/usePermissionMessage';

const ENV_NAME = process.env.REACT_APP_ENV_NAME;
const { Text } = Typography;

export const permissionConfig = {
  mainPermission: 'consulta_pagosexternos',
  required: [
    'view_receptoresdepagos',
    'view_estadodepagosexternos',
    'view_tiposdereferenciasdepagos',
  ],
};

// eslint-disable-next-line react/prop-types
const ConsultaPagos = ({ permission }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [receptores, setReceptores] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const consultaPagosExternos = Form.useWatch('consulta_pagos_externos', form);
  const [tipoDeReferencia, setTipoDeReferencia] = useState([]);
  const [estadosPagos, setEstadosPagos] = useState([]);
  usePermissionMessage(permission);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const _receptores = await getReceptoresDePagos();
      const _estadosDePagos = await getEstadosDePagosExternos();
      if (mounted) {
        setReceptores(_receptores);
        setEstadosPagos(_estadosDePagos);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onFinishConsulta = async () => {
    const values = form.getFieldsValue();
    values.entidad = ENV_NAME === 'nayarit' ? 0 : 1;
    const registros = await consultarRegistros(values);
    if (registros) {
      setData(registros.map((e) => ({
        ...e,
        estado_pagos_externos_id: e.estado_pagos_externos.id,
      })));
    }
  };

  const filter = async () => {
    try {
      setLoading(true);
      const { receptor_de_pagos: receptor_de_pago_emitido } = form.getFieldsValue();
      if (receptor_de_pago_emitido) {
        const referencia = await getTiposDeReferenciaDePagos({ receptor_de_pago_emitido });
        setTipoDeReferencia(referencia);
      } else {
        setTipoDeReferencia([]);
      }
      setLoading(false);
    } catch (error) {
      onError(error);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const params = form.getFieldsValue();
      if (params?.fecha_de_pago) {
        params.inicio = params.fecha_de_pago[0].format(DATE_FORMAT);
        params.fin = params.fecha_de_pago[1].format(DATE_FORMAT);
        delete params.fecha_de_pago;
      }
      const _data = await getPagosExternos(params);
      setData(_data);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const controls = [
    {
      onClick: () => setVisible(false),
      icon: <ArrowLeftOutlined />,
      text: 'Regresar',
    },
  ];

  const columns = [
    {
      titleText: 'Clave de Referencia',
      dataIndex: 'clave_de_referencia_de_pago',
      key: 'clave_de_referencia_de_pago',
      width: 200,
    },
    {
      titleText: 'Nombre del Archivo',
      dataIndex: 'nombre_del_archivo',
      key: 'nombre_del_archivo',
      width: 200,
    },
    {
      titleText: 'Fecha de Pago',
      dataIndex: 'fecha_real_de_pago',
      key: 'fecha_real_de_pago',
      width: 200,
      render: (val) => (val?.format?.(DATE_FRONTEND_FORMAT) || val),
    },
    {
      titleText: 'Fecha de Procesamiento',
      dataIndex: 'fecha_de_aplicacion_procesada',
      key: 'fecha_de_aplicacion_procesada',
      width: 200,
      render: (val) => (val?.format?.(DATE_FRONTEND_FORMAT) || val),
    },
    {
      titleText: 'Importe Cobrado',
      dataIndex: 'importe_cobrado',
      key: 'importe_cobrado',
      width: 200,
      render: (val) => toCurrency(val),
      align: 'right',
    },
    {
      titleText: 'Receptor de Pago',
      dataIndex: 'receptor_de_pago',
      key: 'receptor_de_pago',
      width: 200,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estado_pagos_externos_id',
      key: 'estado_pagos_externos_id',
      width: 200,
      render: (val) => estadosPagos.find((e) => e.id === val)?.descripcion || val,
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 200,
    },
    {
      titleText: 'Notas de Fuente',
      dataIndex: 'notas_de_fuente',
      key: 'notas_de_fuente',
      width: 200,
    },
  ];

  const wipe = () => {
    form.resetFields();
    setData([]);
  };

  const totalAmount = data.reduce((acc, curr) => acc + (curr?.importe_cobrado), 0);
  const notProcessed = data?.filter((e) => e.estado_pagos_externos?.id === 5);
  const processed = data?.filter((e) => e.estado_pagos_externos?.id !== 5);
  const totalProcessed = processed?.reduce((acc, curr) => acc + (curr?.importe_cobrado), 0);
  const totalNotProcessed = notProcessed?.reduce((acc, curr) => acc + (curr?.importe_cobrado), 0);

  const FormDetalle = () => (
    <Row gutter={10}>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="clave_de_referencia_de_pago"
          label="Referencia"
          hasFeedback
        >
          <Input disabled allowClear />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <NumericInput
          name="importe_cobrado"
          label="Importe"
          currency
        />
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="receptor_de_pago_id"
          label="Banco"
          hasFeedback
        >
          <Select
            dataSource={receptores}
          />
        </Form.Item>
      </Col>
    </Row>
  );

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible && selectedRowKeys ? (
          <Card
            bordered={false}
            className="form-container"
            title="Consulta de Pagos"
          >
            <Form
              layout="vertical"
              form={form}
              scrollToFirstError
              initialValues={{ consulta_pagos_externos: false }}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="consulta_pagos_externos"
                    label="¿Consultar por archivo?"
                    hasFeedback
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col>
                {consultaPagosExternos ? (
                  <>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="receptor_de_pagos"
                        label="Banco"
                        hasFeedback
                      >
                        <Select dataSource={receptores} onChange={filter} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="tipo_de_referencia"
                        label="Tipo de Referencia de Pago"
                        hasFeedback
                      >
                        <Select dataSource={tipoDeReferencia} />
                      </Form.Item>
                    </Col>
                    <Uploader
                      title="Archivo a aplicar"
                      limitMB={50}
                      formItemName="archivo"
                      allowedExtensions={['xlsx', 'pdf', 'txt', 'TXT']}
                      allowPreview
                    />
                    <Col xs={24} sm={24} md={8}>
                      <Button
                        onClick={onFinishConsulta}
                        block
                        style={{ marginTop: 21 }}
                      >
                        <MonitorOutlined />
                        <Typography.Text>
                          Consultar archivo
                        </Typography.Text>
                      </Button>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="receptor_de_pago"
                        label="Receptor de Pago (Banco)"
                        hasFeedback
                      >
                        <Select dataSource={receptores} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="fecha_de_pago"
                        label="Fecha de Pago"
                        hasFeedback
                      >
                        <DatePicker.RangePicker
                          placeholder=""
                          format={DATE_FRONTEND_FORMAT}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={4}>
                      <Button
                        style={{ marginTop: 21 }}
                        icon={<SearchOutlined />}
                        onClick={handleSearch}
                      >
                        Consultar Registros
                      </Button>
                    </Col>
                  </>
                )}
              </Row>
            </Form>
            {consultaPagosExternos ? (
              <CustomTable
                cols={columns}
                data={data || []}
                mobileColIndex={0}
                rowSelection={rowSelection}
                handleOnRowClick={handleOnRowClick}
                rowKey="id"
                allowSearch
                customActions={[
                  {
                    icon: <DeleteOutlined />,
                    text: 'Limpiar',
                    onClick: wipe,
                    disabledBySelection: false,
                  },
                ]}
              />
            ) : (
              <CustomTable
                cols={columns}
                data={data || []}
                mobileColIndex={0}
                rowSelection={rowSelection}
                handleOnRowClick={handleOnRowClick}
                rowKey="id"
                allowSearch={false}
                customActions={[
                  {
                    icon: <DeleteOutlined />,
                    text: 'Limpiar',
                    onClick: wipe,
                    disabledBySelection: false,
                  },
                  // {
                  //   icon: <EyeOutlined />,
                  //   text: 'Detalle',
                  //   onClick: retrieveInfo,
                  //   disabledBySelection: !!selectedRowKeys.length,
                  // },
                ]}
              />
            )}
            <br />
            <Row gutter={12}>
              <Col xs={24} sm={24} md={8}>
                <Text>
                  Total de Registros :
                  {' '}
                  {data?.length || 0}
                </Text>
              </Col>
              <Col xs={24} sm={24} md={8} />
              <Col xs={24} sm={24} md={8}>
                <Text style={{ float: 'right' }}>
                  Total :
                  {' '}
                  {toCurrency(totalAmount || 0)}
                </Text>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Text>
                  Total de Registros Rechazados:
                  {' '}
                  {notProcessed?.length || 0}
                </Text>
              </Col>
              <Col xs={24} sm={24} md={8} />
              <Col xs={24} sm={24} md={8}>
                <Text style={{ float: 'right' }}>
                  Total :
                  {' '}
                  {toCurrency(totalNotProcessed || 0)}
                </Text>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Text>
                  Total de Registros Aplicados:
                  {' '}
                  {processed?.length || 0}
                </Text>
              </Col>
              <Col xs={24} sm={24} md={8} />
              <Col xs={24} sm={24} md={8}>
                <Text style={{ float: 'right' }}>
                  Total :
                  {' '}
                  {toCurrency(totalProcessed || 0)}
                </Text>
              </Col>
            </Row>
          </Card>
        ) : (
          <Col span={24}>
            <Card
              bordered={false}
              className="form-container"
              title="Consulta"
              extra={(<FormSubmitCustomControls controls={controls} />)}
            >
              <Col span={24}>
                <Form
                  layout="vertical"
                  form={form}
                  scrollToFirstError
                >
                  <FormDetalle />
                </Form>
              </Col>
            </Card>
          </Col>
        )}
      </Spin>
    </Row>
  );
};
export default ConsultaPagos;
